import { Controller } from "@hotwired/stimulus"

// Phase 2.10 Tier 4 — Document Audit Settings auto-save.
//
// No Save / Cancel buttons. Each change to the form posts the full
// payload via PATCH and the controller renders an inline status
// indicator ("Saving…" / "Saved · 14:03" / "Couldn't save").
//
// CLAUDE.md "Confirmation dialogs": disabling access logging or
// turning off finalize for a doctype that's currently enabled is a
// destructive SOC-2 event. Those changes intercept the auto-save,
// show a styled modal, and on Cancel revert the toggle to its
// previous state instead of persisting. On Apply, the auto-save
// fires as usual.
export default class extends Controller {
  static targets = ["finalizeCheckbox", "accessLoggingDefault",
                    "confirmModal", "confirmMessage", "status"]

  connect() {
    this._timer = null
    this._inFlight = false
  }

  // Bound on the form via `change->document-audit-settings#autoSave`.
  // Every input fires this on commit; we debounce so a flurry of
  // toggles flips into one round-trip.
  autoSave(event) {
    const reasons = this._destructiveReasons(event?.target)
    if (reasons.length > 0) {
      this._pendingDestructive = event?.target
      this.confirmMessageTarget.innerHTML =
        "You're about to make these audit-trail changes:<br><ul style=\"margin-top:8px;list-style:disc;padding-left:18px\">" +
        reasons.map((r) => `<li>${r}</li>`).join("") +
        "</ul>"
      this.confirmModalTarget.classList.remove("hidden")
      this.confirmModalTarget.classList.add("flex")
      return
    }
    this._scheduleSave()
  }

  cancelConfirm(event) {
    if (event) event.preventDefault()
    // Revert the offending input to its prior state — `data-was-checked`
    // is stamped server-side at render time and refreshed after every
    // successful save.
    const target = this._pendingDestructive
    if (target && target.type === "checkbox") {
      target.checked = target.dataset.wasChecked === "true"
    }
    this._pendingDestructive = null
    this.confirmModalTarget.classList.add("hidden")
    this.confirmModalTarget.classList.remove("flex")
  }

  confirmAndSubmit(event) {
    if (event) event.preventDefault()
    this._pendingDestructive = null
    this.confirmModalTarget.classList.add("hidden")
    this.confirmModalTarget.classList.remove("flex")
    this._scheduleSave()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── Internals ───────────────────────────────────────────────────

  _scheduleSave() {
    clearTimeout(this._timer)
    this._timer = setTimeout(() => this._save(), 250)
  }

  async _save() {
    if (this._inFlight) {
      this._scheduleSave()
      return
    }
    this._inFlight = true
    this._renderStatus("Saving…", "muted")

    const form = this.element.tagName === "FORM" ? this.element : this.element.closest("form")
    if (!form) {
      this._renderStatus("No form to save", "error")
      this._inFlight = false
      return
    }

    const body = new FormData(form)
    try {
      const resp = await fetch(form.action, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": this._csrfToken(),
          "Accept": "application/json",
        },
        body,
      })
      if (!resp.ok) {
        const text = await resp.text().catch(() => "")
        this._renderStatus("Couldn't save", "error")
        console.warn("[document-audit-settings] save failed:", resp.status, text)
        return
      }
      this._renderStatus(`Saved · ${this._stamp()}`, "ok")
      this._refreshWasChecked()
    } catch (e) {
      this._renderStatus("Network error", "error")
    } finally {
      this._inFlight = false
    }
  }

  // After a successful save, refresh `data-was-checked` on every
  // toggle so the next destructive-change check compares against the
  // freshly-persisted state (not the page-load state).
  _refreshWasChecked() {
    if (this.hasFinalizeCheckboxTarget) {
      this.finalizeCheckboxTargets.forEach((cb) => {
        cb.dataset.wasChecked = cb.checked ? "true" : "false"
      })
    }
    if (this.hasAccessLoggingDefaultTarget) {
      const cb = this.accessLoggingDefaultTarget
      cb.dataset.wasChecked = cb.checked ? "true" : "false"
    }
  }

  _destructiveReasons(eventTarget) {
    const reasons = []
    if (this.hasAccessLoggingDefaultTarget) {
      const cb = this.accessLoggingDefaultTarget
      const wasOn = cb.dataset.wasChecked === "true"
      const matches = (eventTarget == null) || (eventTarget === cb)
      if (wasOn && !cb.checked && matches) {
        reasons.push("Turning OFF access logging company-wide — every view and download will stop being recorded.")
      }
    }
    if (this.hasFinalizeCheckboxTarget) {
      this.finalizeCheckboxTargets.forEach((cb) => {
        const wasOn = cb.dataset.wasChecked === "true"
        const matches = (eventTarget == null) || (eventTarget === cb)
        if (wasOn && !cb.checked && matches) {
          const label = cb.closest("label")?.querySelector("span")?.textContent?.trim() || cb.name
          reasons.push(`Disabling finalize for <strong>${label}</strong> — existing Final docs stay locked, but new ones can't be finalized.`)
        }
      })
    }
    return reasons
  }

  _renderStatus(text, kind) {
    if (!this.hasStatusTarget) return
    let cls = "text-gray-400"
    if (kind === "ok")    cls = "text-emerald-600"
    if (kind === "error") cls = "text-rose-600"
    this.statusTarget.className = `text-[12px] mt-1 shrink-0 ${cls}`
    this.statusTarget.textContent = text
  }

  _stamp() {
    const d = new Date()
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  }

  _csrfToken() {
    const el = document.querySelector('meta[name="csrf-token"]')
    return el ? el.content : ""
  }
}
